'use client';

import {
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  Wrench,
  X,
} from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from './ui/alert';

export type SystemHealthStatus =
  | 'operational'
  | 'degraded'
  | 'partial_outage'
  | 'major_outage'
  | 'maintenance';

export interface SystemHealthBannerProps {
  status: SystemHealthStatus;
  title?: string;
  message?: string;
  statusPageUrl?: string;
  onDismiss?: () => void;
  componentsAffected?: string[];
}

const DEFAULT_STATUS_PAGE_URL = 'https://status.letsorderup.com';

/**
 * The shadcn Alert primitive variants — kept in lockstep with
 * `packages/common/src/components/ui/alert.tsx` (cva variants `default`,
 * `destructive`, `warning`). New variants must be added there before
 * being referenced here.
 */
type AlertVariant = 'default' | 'destructive' | 'warning';

interface VariantConfig {
  /**
   * Which shadcn Alert variant this status maps to. `warning` covers the
   * amber/orange tier; `destructive` covers red (major outage); `default`
   * covers maintenance (informational, distinct from warning/error).
   */
  alertVariant: AlertVariant;
  /** Tailwind classes layered onto the Alert root for palette refinement. */
  container: string;
  /** Tailwind classes for the icon tint (also referenced by tests). */
  accent: string;
  /** Default title (used when the consumer doesn't override). */
  defaultTitle: string;
  /** ARIA role appropriate for the variant. */
  ariaRole: 'status' | 'alert';
  /** ARIA live priority appropriate for the variant. */
  ariaLive: 'polite' | 'assertive' | 'off';
}

/**
 * Pure mapping from the high-level system-health status to its banner
 * presentation. Exported separately so the variant table can be unit-tested
 * without rendering React (the @order-up/common package has no jsdom +
 * @testing-library/react setup; tests run in node and assert on this map).
 *
 * Wave D R9: variant config now carries an `alertVariant` field that maps
 * to the shadcn `Alert` primitive's cva variants. The banner renders an
 * `<Alert variant={alertVariant}>` and layers per-status palette refinements
 * via `container` so the final colors meet the existing WCAG-AA token
 * pairings (bg-X-100 / text-X-900 family) the tests pin.
 */
export function getSystemHealthBannerVariant(
  status: SystemHealthStatus,
): VariantConfig | null {
  switch (status) {
    case 'operational':
      return null;
    case 'degraded':
      return {
        alertVariant: 'warning',
        container:
          'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-700',
        accent: 'text-amber-700 dark:text-amber-300',
        defaultTitle: 'Some services are degraded',
        ariaRole: 'status',
        ariaLive: 'polite',
      };
    case 'partial_outage':
      return {
        alertVariant: 'warning',
        container:
          'bg-orange-100 text-orange-900 border-orange-300 dark:bg-orange-900/30 dark:text-orange-100 dark:border-orange-700',
        accent: 'text-orange-700 dark:text-orange-300',
        defaultTitle: 'Partial outage in progress',
        ariaRole: 'alert',
        ariaLive: 'assertive',
      };
    case 'major_outage':
      return {
        alertVariant: 'destructive',
        container:
          'bg-red-100 text-red-900 border-red-300 dark:bg-red-900/30 dark:text-red-100 dark:border-red-700',
        accent: 'text-red-700 dark:text-red-300',
        defaultTitle: 'Major outage in progress',
        ariaRole: 'alert',
        ariaLive: 'assertive',
      };
    case 'maintenance':
      return {
        alertVariant: 'default',
        container:
          'bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-900/30 dark:text-blue-100 dark:border-blue-700',
        accent: 'text-blue-700 dark:text-blue-300',
        defaultTitle: 'Scheduled maintenance',
        ariaRole: 'status',
        ariaLive: 'polite',
      };
  }
}

function VariantIcon({
  status,
  className,
}: {
  status: Exclude<SystemHealthStatus, 'operational'>;
  className: string;
}) {
  switch (status) {
    case 'degraded':
      return <AlertTriangle className={className} aria-hidden="true" />;
    case 'partial_outage':
      return <AlertOctagon className={className} aria-hidden="true" />;
    case 'major_outage':
      return <AlertCircle className={className} aria-hidden="true" />;
    case 'maintenance':
      return <Wrench className={className} aria-hidden="true" />;
  }
}

/**
 * Top-of-app status banner. Hidden when status is `operational`. Otherwise
 * renders an ARIA-live region linking to the public status page.
 *
 * Wave D R9: built on top of the shadcn `Alert` primitive (Title +
 * Description slots) so layout/role/structure stay aligned with the rest of
 * the admin UI. Per-status palette refinements layer onto Alert's cva via
 * `className`. The dismiss button is a positioned overlay because Alert
 * doesn't ship one.
 */
export function SystemHealthBanner({
  status,
  title,
  message,
  statusPageUrl = DEFAULT_STATUS_PAGE_URL,
  onDismiss,
  componentsAffected,
}: SystemHealthBannerProps) {
  const variant = getSystemHealthBannerVariant(status);
  if (!variant) return null;

  const resolvedTitle = title ?? variant.defaultTitle;
  const affected =
    componentsAffected && componentsAffected.length > 0
      ? componentsAffected.join(', ')
      : null;

  return (
    <Alert
      variant={variant.alertVariant}
      role={variant.ariaRole}
      aria-live={variant.ariaLive}
      className={`sticky top-0 z-[9998] rounded-none border-x-0 border-t-0 ${variant.container}`}
    >
      <a
        href={statusPageUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="contents hover:underline focus:outline-none focus:underline"
      >
        <VariantIcon
          status={status as Exclude<SystemHealthStatus, 'operational'>}
          className={`size-4 ${variant.accent}`}
        />
        <AlertTitle>{resolvedTitle}</AlertTitle>
        {(message || affected) && (
          <AlertDescription>
            {message ? <span>{message}</span> : null}
            {affected ? (
              <span className="text-xs opacity-80">Affected: {affected}</span>
            ) : null}
          </AlertDescription>
        )}
      </a>

      {onDismiss ? (
        <button
          type="button"
          aria-label="Dismiss banner"
          onClick={onDismiss}
          className={`absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-sm transition-colors hover:bg-black/10 focus:outline-none focus:ring-2 focus:ring-current ${variant.accent}`}
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ) : null}
    </Alert>
  );
}
